import {
  getZodDef,
  integrationSchemaRegistry,
  isOptionalSchema,
  type ZodShape,
} from '@main/shared/utils';
import { TFunction } from 'i18next';
import z from 'zod/v4';
import {
  $ZodArrayDef,
  $ZodDiscriminatedUnionDef,
  $ZodEnumDef,
  $ZodObjectDef,
  $ZodUnionDef,
} from 'zod/v4/core';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslateFunction = TFunction<any>;

export function generateSchemaDocumentation(
  t: TranslateFunction,
  tKey: string,
  schema: z.ZodObject,
): string {
  return processFieldGroup(t, tKey, schema.shape, 0).join('\n\n').trim();
}

function processFieldGroup(
  t: TranslateFunction,
  tKey: string,
  shape: ZodShape,
  depth: number,
): string[] {
  const results: string[] = [];

  for (const [fieldName, fieldSchema] of Object.entries(shape)) {
    const fieldTKey = `${tKey}.${fieldName}`;
    const output = processField({ t, tKey: fieldTKey, schema: fieldSchema, depth });
    if (output) {
      results.push(output);
    }
  }

  return results;
}

type FieldInput = {
  t: TranslateFunction;
  tKey: string;
  schema: z.ZodType;
  depth: number;
  isOptional?: boolean;
};

function processField(input: FieldInput): string {
  const { t, tKey, schema, depth } = input;
  const def = getZodDef(schema);
  const isOptional = input.isOptional ?? isOptionalSchema(schema);
  const registryEntry = integrationSchemaRegistry.get(schema);

  switch (def.type) {
    case 'optional': {
      return processField({
        ...input,
        schema: def.innerType as z.ZodType,
        isOptional: true,
      });
    }
    case 'enum': {
      return buildFieldContent(t, tKey, depth, isOptional, getEnumBullets(t, tKey, schema, def));
    }
    case 'union': {
      if (isDiscriminatedUnionDef(def)) {
        return buildFieldContent(
          t,
          tKey,
          depth,
          isOptional,
          null,
          processUnionOptions(t, tKey, def, depth),
        );
      }
      return buildFieldContent(t, tKey, depth, isOptional);
    }
    case 'object': {
      const nestedTKey = registryEntry?.i18n ?? tKey;
      return buildFieldContent(
        t,
        tKey,
        depth,
        isOptional,
        null,
        getObjectNestedContent(t, nestedTKey, def, depth),
      );
    }
    case 'array': {
      return processArrayField(input, def, isOptional);
    }
    default: {
      return buildFieldContent(t, tKey, depth, isOptional);
    }
  }
}

function processArrayField(input: FieldInput, def: $ZodArrayDef, isOptional: boolean): string {
  const { t, tKey, depth } = input;
  const elementSchema = def.element as z.ZodType;
  const elementDef = getZodDef(elementSchema);

  switch (elementDef.type) {
    case 'enum': {
      return buildFieldContent(
        t,
        tKey,
        depth,
        isOptional,
        getEnumBullets(t, tKey, elementSchema, elementDef),
      );
    }
    case 'object': {
      const { i18n: elementTKey = tKey } = integrationSchemaRegistry.get(elementSchema) ?? {};
      return buildFieldContent(
        t,
        tKey,
        depth,
        isOptional,
        null,
        getObjectNestedContent(t, elementTKey, elementDef, depth),
      );
    }
    case 'union': {
      if (isDiscriminatedUnionDef(elementDef)) {
        return buildFieldContent(
          t,
          tKey,
          depth,
          isOptional,
          null,
          processUnionOptions(t, tKey, elementDef, depth),
        );
      }
      return buildFieldContent(t, tKey, depth, isOptional);
    }
    default: {
      return buildFieldContent(t, tKey, depth, isOptional);
    }
  }
}

function buildFieldContent(
  t: TranslateFunction,
  tKey: string,
  depth: number,
  isOptional: boolean,
  inlineContent?: string | null,
  nestedContent?: string | null,
): string {
  const name = t(`${tKey}.name`, { defaultValue: tKey.split('.').pop() ?? tKey });
  const description = t(`${tKey}.description`, { defaultValue: '' });
  const header = makeHeader(name, depth, isOptional);

  const inlineParts: string[] = [header];
  if (description) {
    inlineParts.push(description);
  }
  if (inlineContent) {
    inlineParts.push(inlineContent);
  }

  const result = inlineParts.join('\n');
  if (!nestedContent) {
    return result;
  }
  return result + '\n\n' + nestedContent;
}

function getEnumBullets(
  t: TranslateFunction,
  tKey: string,
  schema: z.ZodType,
  def: $ZodEnumDef,
): string {
  const { i18n: enumTKey = tKey } = integrationSchemaRegistry.get(schema) ?? {};
  const enumValues = Object.values(def.entries).filter((v): v is string => typeof v === 'string');
  return formatEnumValues(t, enumTKey, enumValues);
}

function getObjectNestedContent(
  t: TranslateFunction,
  tKey: string,
  def: $ZodObjectDef,
  depth: number,
): string | null {
  const hasFields = Object.keys(def.shape).length > 0;
  if (!hasFields) {
    return null;
  }

  const nestedLines = processFieldGroup(t, `${tKey}.fields`, def.shape as ZodShape, depth + 1);
  return nestedLines.length > 0 ? nestedLines.join('\n\n') : null;
}

function formatEnumValues(t: TranslateFunction, tKey: string, values: string[]): string {
  return values
    .map((value) => {
      const label = t(`${tKey}.${value}`, { defaultValue: value });
      return `- ${label}`;
    })
    .join('\n');
}

function makeHeader(text: string, depth: number, isOptional: boolean): string {
  const cappedDepth = Math.min(depth, 4);
  const hashes = '#'.repeat(cappedDepth + 2);
  const optionalSuffix = isOptional ? ' (Optional)' : '';
  return `${hashes} ${text}${optionalSuffix}`;
}

function processUnionOptions(
  t: TranslateFunction,
  tKey: string,
  def: $ZodDiscriminatedUnionDef,
  depth: number,
): string {
  const sections: string[] = [];

  for (const option of def.options) {
    const optionDef = getZodDef(option);
    if (optionDef.type !== 'object') continue;

    const discriminator = optionDef.shape[def.discriminator];
    if (!discriminator) continue;

    const discriminatorDef = getZodDef(discriminator);
    const discriminatorI18nKey = integrationSchemaRegistry.get(discriminator)?.i18n;

    const discriminatorValues = (() => {
      switch (discriminatorDef.type) {
        case 'literal':
          return discriminatorDef.values;
        case 'enum':
          return Object.values(discriminatorDef.entries);
        default:
          return [];
      }
    })().filter((v): v is string => typeof v === 'string');

    if (discriminatorValues.length === 0) continue;

    const optionI18nKey =
      integrationSchemaRegistry.get(option)?.i18n ??
      `${tKey}.options.${discriminatorValues.join('|')}`;

    const { [def.discriminator]: _, ...optionShape } = optionDef.shape;

    for (const value of discriminatorValues) {
      const label = discriminatorI18nKey
        ? t(`${discriminatorI18nKey}.${value}`, { defaultValue: value })
        : value;

      const optionHeader = makeHeader(`Option: ${label}`, depth + 1, false);
      const optionDescription = t(`${optionI18nKey}.description`, { defaultValue: '' });
      const optionParts: string[] = [optionHeader];

      if (optionDescription) {
        optionParts.push(optionDescription);
      }

      if (Object.keys(optionShape).length > 0) {
        const nestedLines = processFieldGroup(t, optionI18nKey, optionShape as ZodShape, depth + 2);
        if (nestedLines.length > 0) {
          optionParts.push(nestedLines.join('\n\n'));
        }
      }

      sections.push(optionParts.join('\n\n'));
    }
  }

  return sections.join('\n\n');
}

function isDiscriminatedUnionDef(def: $ZodUnionDef): def is $ZodDiscriminatedUnionDef {
  return 'discriminator' in def && typeof def.discriminator === 'string';
}
